/**
 * `@eis/authservice-sdk` — the generated client for the Identity Administration API.
 *
 * Scope note: authservice is NOT on the data path. Every service validates tokens locally
 * against JWKS (decision T-3). This SDK covers user provisioning and the tenant↔user mapping —
 * administration — and, since T-42 (SDD-019), the browser-session token handler of the Keycloak
 * environments: `createSessionApi` (./session) exchanges the PKCE code, refreshes and ends the
 * session, with the refresh token only ever in an HttpOnly cookie.
 *
 * Everything under `./generated` comes from api/openapi/auth.v1.openapi.yaml via
 * `pnpm gen:sdk`. Never hand-edit it — `make gen-check` fails on drift (rule 26). The types are
 * re-exported wholesale rather than through a curated list, because a curated list is a second
 * copy of the contract and would be the thing that goes stale.
 */
import { createEisClient, type EisClient, type EisClientOptions } from '@eis/sdk-runtime';
import type { paths } from './generated/auth.v1';
import { isSessionEndpoint } from './session-paths';

export type * from './generated/auth.v1';

/** The concurrency and tracing contract travels with the client, not beside it (T-12/T-13). */
export {
  ConflictError,
  isConflictError,
  createEtagStore,
  type ConflictKind,
  type EtagStore,
  type TokenProvider,
  type TraceparentProvider,
  type EisClientOptions,
  createSessionRefreshMiddleware,
  isSafeToReplay,
  type SessionRefreshOptions,
} from '@eis/sdk-runtime';

// The browser-session paths (T-42, SDD-019). The session API itself is its own entry,
// `@eis/authservice-sdk/session`, loaded lazily by the OIDC adapter (see ./session).
export { isSessionEndpoint, SESSION_PATHS } from './session-paths';
export type { ExchangeInput, SessionApi, SessionEnvelope, SessionRefusalReason } from './session';

export type AuthServiceClient = EisClient<paths>;

/**
 * The session endpoints are never acted on by the session middleware (S-6): this client knows
 * them, so it says so by default, whatever else the caller passes.
 */
export function createAuthServiceClient(options: EisClientOptions): AuthServiceClient {
  const callerSays = options.isSessionEndpoint;
  return createEisClient<paths>({
    ...options,
    isSessionEndpoint: (url) => isSessionEndpoint(url) || (callerSays?.(url) ?? false),
  });
}
